type Props = { data: Record<string, unknown> | Array<Record<string, unknown>> };

export function JsonLd({ data }: Props) {
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

import { SITE, abs } from "@/lib/seo/site";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE.url,
    logo: SITE.logo,
    sameAs: [],
    contactPoint: [{ "@type": "ContactPoint", email: SITE.email, contactType: "customer support", areaServed: "IT", availableLanguage: ["Italian"] }],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.url,
    inLanguage: SITE.language,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function softwareAppSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE.name,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "LegalTech",
    operatingSystem: "Web",
    description: SITE.description,
    offers: { "@type": "Offer", price: "79", priceCurrency: "EUR" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "127" },
  };
}

export function breadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.path),
    })),
  };
}

export function faqSchema(items: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleSchema(args: { title: string; description: string; path: string; datePublished: string; dateModified?: string; image?: string; author?: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.title,
    description: args.description,
    mainEntityOfPage: abs(args.path),
    datePublished: args.datePublished,
    dateModified: args.dateModified ?? args.datePublished,
    image: args.image ?? SITE.ogImage,
    author: { "@type": "Organization", name: args.author ?? SITE.name },
    publisher: { "@type": "Organization", name: SITE.name, logo: { "@type": "ImageObject", url: SITE.logo } },
  };
}

export function definedTermSchema(args: { name: string; description: string; path: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: args.name,
    description: args.description,
    inDefinedTermSet: `${SITE.url}/glossario`,
    url: abs(args.path),
  };
}

/** Schema LegalService — segnala a Google/LLM che l'app eroga servizi legali AI. */
export function legalServiceSchema(args?: { name?: string; description?: string; path?: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    name: args?.name ?? SITE.name,
    description: args?.description ?? SITE.description,
    url: args?.path ? abs(args.path) : SITE.url,
    areaServed: { "@type": "Country", name: "Italia" },
    serviceType: "Ricerca legale assistita da AI, redazione atti, analisi documentale",
    provider: { "@type": "Organization", name: SITE.name, url: SITE.url, logo: SITE.logo },
  };
}

/** Schema HowTo — ottimo per AEO (Answer Engine Optimization) e featured snippet. */
export function howToSchema(args: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string }>;
  totalTime?: string; // ISO 8601 duration es. "PT5M"
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: args.name,
    description: args.description,
    ...(args.totalTime ? { totalTime: args.totalTime } : {}),
    step: args.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/** Schema Speakable — ottimizza la pagina per Google Assistant e voice search. */
export function speakableSchema(selectors: string[] = ["h1", "h2", ".tldr"]) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: selectors,
    },
  };
}

/** Schema Service generico per pagine di feature/area. */
export function serviceSchema(args: { name: string; description: string; path: string; category?: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: args.name,
    description: args.description,
    url: abs(args.path),
    provider: { "@type": "Organization", name: SITE.name, url: SITE.url },
    areaServed: { "@type": "Country", name: "Italia" },
    ...(args.category ? { category: args.category } : {}),
  };
}

/** Schema Product+Offer per piano tariffario. */
export function offerSchema(args: { name: string; price: number; currency?: string; description?: string; url?: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: args.name,
    description: args.description ?? `${args.name} — piano LexAIPro per professionisti legali`,
    brand: { "@type": "Brand", name: SITE.name },
    offers: {
      "@type": "Offer",
      price: String(args.price),
      priceCurrency: args.currency ?? "EUR",
      availability: "https://schema.org/InStock",
      url: args.url ? abs(args.url) : SITE.url,
    },
  };
}
